import React, { useMemo } from 'react'
import { Form } from 'antd'
import { useStore, useUnit } from 'effector-react'
import dayjs, { Dayjs } from 'dayjs'
import {
  $CommissionPaymentForm,
  addCommissionPayment,
  addCommissionPaymentMounted,
  deleteCommissionPayment,
  deleteCommissionPaymentMounted,
  editCommissionPayment,
  editCommissionPaymentMounted,
  getCommissionPaymentById,
  getCommissionPaymentByIdMounted,
  setCommissionPaymentForm,
} from './model'
import { $userProfile } from '../../../../../features/user'
import { transactionType } from '../../../../../libs/finSystem'
import {
  formatTransactionDateTime,
  parseNaiveDateTime,
} from '../../../../../libs/utils'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { $params } from '../../../../../features/router'
import { initialCommissionModalState } from './const'
import { ModalType } from '../types'
import { $wallet as $currentWallet } from '../../details/model'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { useTransactionForm } from '../useTransactionForm'
import {
  AmountWithCurrency,
  CommentField,
  DateTimeRow,
  ExchangeRateField,
  LockedTypeSelect,
  ProductSelect,
  WalletSelect,
} from '../transactionFields'

type Props = {
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  transactionId?: string
  type: ModalType
}

type FormValues = {
  walletId: string
  product: string
  amount: string
  currency: string
  exchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  comment: string
}

export const CommissionPaymentTransactionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCancel,
  transactionId,
  mode = 'create',
  type,
}) => {
  const [form] = Form.useForm<FormValues>()

  const userProfile = useStore($userProfile)
  const wallets = useUnit($walletAllowedList)
  const currentWallet = useStore($currentWallet)
  const params = useStore($params)

  const walletId = Form.useWatch('walletId', form)

  const fromWalletCurrencies = useMemo(() => {
    if (currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const w = wallets.find((item) => Number(item.id) === Number(walletId))
    return w?.currencies ?? []
  }, [wallets, walletId, currentWallet])

  const initialWalletId =
    params?.walletID ||
    (type === 'wallet' && currentWallet.id ? String(currentWallet.id) : '')

  const {
    storeData: commissionForm,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleCancel,
    handleDelete,
    isSubmitting,
    isDeleting,
    isLoadingForm,
  } = useTransactionForm({
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store: $CommissionPaymentForm,
    initialState: initialCommissionModalState,
    setForm: setCommissionPaymentForm,
    mapStoreToForm: (data) => {
      const dt = parseNaiveDateTime(data.dateTime)
      return {
        walletId: String(data.walletId ?? ''),
        product: data.product,
        amount: String(data.amount ?? ''),
        currency: data.currency,
        exchangeRate: String(data.exchangeRate ?? ''),
        date: dt ? dayjs(dt.date) : dayjs(),
        time: dt ? dayjs(dt.time, 'HH:mm:ss') : dayjs(),
        comment: data.comment,
      }
    },
    events: {
      addMount: addCommissionPaymentMounted,
      editMount: editCommissionPaymentMounted,
      deleteMount: deleteCommissionPaymentMounted,
      getByIdMount: getCommissionPaymentByIdMounted,
    },
    effects: {
      add: addCommissionPayment,
      edit: editCommissionPayment,
      delete: deleteCommissionPayment,
      getById: getCommissionPaymentById,
    },
    buildPayload: (values, { mode: m, transactionId: tid }) => {
      const wallet =
        values.walletId || (currentWallet.id ? String(currentWallet.id) : '')
      return {
        ...values,
        id: m === 'edit' ? tid : '',
        walletId: wallet,
        exchangeRate: values.exchangeRate || '0',
        dateTime: formatTransactionDateTime(
          values.date!.format('YYYY-MM-DD'),
          values.time!.format('HH:mm:ss'),
        ),
      } as any
    },
  })

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${mode === 'edit' ? 'Edit' : 'Create'} commission${commissionForm.id ? ` #${commissionForm.id}` : ''}`}
      mode={mode}
      creatorEmail={userProfile.email}
      isLoading={isLoadingForm}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      onSubmit={handleSubmit}
      onSubmitAndCreateNew={
        mode === 'create' ? handleSubmitAndCreateNew : undefined
      }
      onDelete={mode === 'edit' ? handleDelete : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          walletId: initialWalletId,
          date: dayjs(),
          time: dayjs(),
        }}
        requiredMark={false}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <WalletSelect
            name="walletId"
            label="Wallet"
            required
            disabled={type === 'wallet' || !!params?.walletID}
            style={{ flex: 1 }}
          />
          <LockedTypeSelect
            value={transactionType[5].value}
            options={transactionType}
            style={{ flex: 1 }}
          />
        </div>

        <ProductSelect name="product" />

        <AmountWithCurrency
          amountName="amount"
          currencyName="currency"
          amountLabel="Amount"
          currencies={fromWalletCurrencies}
          required
          allowNegative
          currencyRequired
          currencyWidth={160}
        />

        <ExchangeRateField name="exchangeRate" />

        <DateTimeRow />

        <CommentField required />
      </Form>
    </TransactionModalLayout>
  )
}
